/**
 * ============================================================
 * columnsSlice.ts — Configuracao de Colunas de Tabelas
 * ============================================================
 *
 * Responsabilidade: Gerenciar a configuracao de colunas visiveis,
 *   ordenacao e visibilidade nas tabelas de Leads e Clientes.
 *   Permite que cada usuario personalize quais colunas ve e em
 *   que ordem, persistindo no localStorage.
 * Arquitetura: Slice do Zustand com persistencia localStorage.
 *   As configuracoes sao independentes por tipo (leads vs clientes)
 *   e armazenadas com chaves separadas no localStorage.
 * Padroes: Zustand Slice, Persistencia localStorage, Helper
 *   reutilizavel (reorderByIndex) para drag-and-drop.
 * Dependencias:
 *   - Depende de: ../types, ../persistence, ../helpers
 *   - Quem depende dele: useStore, tabelas de leads/clientes,
 *     modais de configuracao de colunas
 *
 * ============================================================
 */

import type { StateCreator } from 'zustand';
import type { LeadsColumnConfig } from '../types';
import { loadPersisted, persist } from '../persistence';
import { reorderByIndex } from '../helpers';

// ── Configuracao Padrao de Colunas de Leads ──
// Define todas as colunas disponiveis com visibilidade inicial
// (visible: true = visivel por padrao, false = oculta ate o usuario ativar)
export const DEFAULT_LEADS_COLUMNS: LeadsColumnConfig[] = [
  { id: 'company', label: 'Lead / Contato', visible: true, order: 0, sortable: true },
  { id: 'origin', label: 'Fonte', visible: true, order: 1, sortable: true },
  { id: 'service', label: 'Serviço', visible: true, order: 2, sortable: true },
  { id: 'column', label: 'Estágio', visible: true, order: 3, sortable: true },
  { id: 'funnelTrack', label: 'Funil', visible: false, order: 21, sortable: false },
  { id: 'score', label: 'Score', visible: true, order: 4, sortable: true },
  { id: 'value', label: 'Valor', visible: true, order: 5, sortable: true },
  { id: 'phone', label: 'Telefone', visible: false, order: 6, sortable: true },
  { id: 'sdr', label: 'Pré-Vendedor (SDR)', visible: false, order: 7, sortable: true },
  { id: 'closer', label: 'Vendedor (Closer)', visible: false, order: 8, sortable: true },
  { id: 'clientCategory', label: 'Categoria / Indústria', visible: false, order: 9, sortable: true },
  { id: 'porte', label: 'Porte da Empresa', visible: false, order: 10, sortable: true },
  { id: 'priority', label: 'Prioridade', visible: false, order: 11, sortable: true },
  { id: 'utm_source', label: 'UTM Source', visible: false, order: 12, sortable: true },
  { id: 'utm_medium', label: 'UTM Medium', visible: false, order: 13, sortable: true },
  { id: 'territory', label: 'Território', visible: false, order: 14, sortable: true },
  { id: 'estado', label: 'Estado', visible: true, order: 15, sortable: true },
  { id: 'pais', label: 'País', visible: true, order: 16, sortable: true },
  { id: 'createdAt', label: 'Criado em', visible: true, order: 17, sortable: true },
  { id: 'lastStatusDate', label: 'Último Update', visible: true, order: 18, sortable: true },
  { id: 'responsible', label: 'Responsável', visible: true, order: 19, sortable: true },
];

// ── Configuracao Padrao de Colunas de Clientes ──
// Colunas especificas para a visao de carteira de clientes (pos-venda)
export const DEFAULT_CLIENTS_COLUMNS: LeadsColumnConfig[] = [
  { id: 'company', label: 'Empresa', visible: true, order: 0, sortable: true },
  { id: 'contact', label: 'Contato', visible: true, order: 1, sortable: true },
  { id: 'products', label: 'Produtos', visible: true, order: 2, sortable: false },
  { id: 'sdr', label: 'SDR', visible: true, order: 3, sortable: true },
  { id: 'closer', label: 'Closer', visible: true, order: 4, sortable: true },
  { id: 'contract_value', label: 'Contrato', visible: true, order: 5, sortable: true },
  { id: 'mrr', label: 'MRR', visible: true, order: 6, sortable: true },
  { id: 'health', label: 'Saúde', visible: true, order: 7, sortable: true },
  { id: 'status', label: 'Status', visible: true, order: 8, sortable: true },
  { id: 'category', label: 'Categoria', visible: false, order: 9, sortable: true },
  { id: 'porte', label: 'Porte', visible: false, order: 10, sortable: true },
  { id: 'industry', label: 'Indústria', visible: false, order: 11, sortable: true },
  { id: 'contract_dates', label: 'Datas Contrato', visible: false, order: 12, sortable: true },
  { id: 'created_at', label: 'Criado em', visible: false, order: 13, sortable: true },
  { id: 'tags', label: 'Tags', visible: false, order: 14, sortable: false },
];

// ── Interface do Slice ──

export interface ColumnsSlice {
  leadsColumns: LeadsColumnConfig[];
  setLeadsColumns: (cols: LeadsColumnConfig[]) => void;
  toggleLeadsColumn: (id: string) => void;
  reorderLeadsColumns: (fromIndex: number, toIndex: number) => void;
  clientsColumns: LeadsColumnConfig[];
  setClientsColumns: (cols: LeadsColumnConfig[]) => void;
  toggleClientsColumn: (id: string) => void;
}

// ── Criacao do Slice ──
export const createColumnsSlice: StateCreator<ColumnsSlice, [], [], ColumnsSlice> = (set) => ({
  // ── Colunas de Leads ──
  // Carrega do localStorage; se nao existir, usa defaults
  leadsColumns: loadPersisted<LeadsColumnConfig[]>('bl-leadsColumns', DEFAULT_LEADS_COLUMNS),
  setLeadsColumns: (cols) => {
    persist('bl-leadsColumns', cols); // Persiste antes de atualizar estado (write-through)
    set({ leadsColumns: cols });
  },
  // Alterna visibilidade de uma coluna especifica
  toggleLeadsColumn: (id) =>
    set((s) => {
      const updated = s.leadsColumns.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c));
      persist('bl-leadsColumns', updated);
      return { leadsColumns: updated };
    }),
  // Reordena colunas via drag-and-drop
  reorderLeadsColumns: (fromIndex, toIndex) =>
    set((s) => {
      const updated = reorderByIndex(s.leadsColumns, fromIndex, toIndex);
      persist('bl-leadsColumns', updated);
      return { leadsColumns: updated };
    }),

  // ── Colunas de Clientes ──
  // Mesma logica das colunas de leads, com configuracao separada
  clientsColumns: loadPersisted<LeadsColumnConfig[]>('bl-clientsColumns', DEFAULT_CLIENTS_COLUMNS),
  setClientsColumns: (cols) => {
    persist('bl-clientsColumns', cols);
    set({ clientsColumns: cols });
  },
  toggleClientsColumn: (id) =>
    set((s) => {
      const updated = s.clientsColumns.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c));
      persist('bl-clientsColumns', updated);
      return { clientsColumns: updated };
    }),
});
